import type { Messages } from '../../modules/i18n/messages';

/**
 * Shared component-primitive message catalog (Task 7m of the Fun Mode string
 * sweep). Covers hardcoded DEFAULT copy inside `components/ui/*` (design
 * system primitives) and the shared feature components that reuse them
 * (`components/kafka/*`, `components/deployments/*`). Text that arrives via a
 * caller-supplied prop is NOT keyed here — only a component's own default/
 * fallback copy and always-literal chrome (aria-labels, headers, empty
 * states). Server/dynamic data (env names, tag names, error messages, chip
 * values typed by the user) is interpolated via `{param}`, never keyed.
 */
export const uiMessages: Messages = {
  // ---- RefreshButton.tsx ----
  'ui.refreshButton.label': { en: 'Refresh', fun: 'Zhoosh it' },
  'ui.refreshButton.lastSync': {
    en: 'Last sync {time}',
    fun: 'Last vibe check {time}',
  },

  // ---- Pagination.tsx ----
  'ui.pagination.ariaLabel': { en: 'Pagination', fun: 'Page hop' },
  'ui.pagination.previous': { en: 'Previous', fun: 'Back it up' },
  'ui.pagination.next': { en: 'Next', fun: 'Onward' },
  'ui.pagination.goToPage': {
    en: 'Go to page {page}',
    fun: 'Jump to page {page}',
  },
  'ui.pagination.pageSizeLabel': { en: 'Rows per page', fun: 'Rows per page' },
  'ui.pagination.pageSizeOption': {
    en: '{size} / page',
    fun: '{size} / page',
  },

  // ---- DataTable.tsx ----
  'ui.dataTable.emptyText': {
    en: 'Nothing to show.',
    fun: 'Crickets. Nothing to show.',
  },
  'ui.dataTable.sortAscending': { en: 'sorted ascending', fun: 'sorted low to high' },
  'ui.dataTable.sortDescending': { en: 'sorted descending', fun: 'sorted high to low' },

  // ---- ConfirmDialog.tsx ----
  'ui.confirmDialog.confirm': { en: 'Confirm', fun: 'Do it' },
  'ui.confirmDialog.cancel': { en: 'Cancel', fun: 'Nevermind' },

  // ---- SearchableSelect.tsx ----
  'ui.searchableSelect.placeholder': { en: 'Search…', fun: 'Snoop around…' },
  'ui.searchableSelect.noOptionsText': {
    en: 'No matches',
    fun: 'Tumbleweeds. No matches',
  },

  // ---- SearchableMultiSelect.tsx ----
  'ui.searchableMultiSelect.placeholder': {
    en: 'Select…',
    fun: 'Pick some…',
  },
  'ui.searchableMultiSelect.searchPlaceholder': {
    en: 'Search…',
    fun: 'Snoop around…',
  },
  'ui.searchableMultiSelect.noOptionsText': {
    en: 'No matches',
    fun: 'Tumbleweeds. No matches',
  },
  'ui.searchableMultiSelect.searchAriaLabel': {
    en: 'Search options',
    fun: 'Options hunt',
  },
  'ui.searchableMultiSelect.clearSelection': {
    en: 'Clear selection',
    fun: 'Wipe it clean',
  },
  'ui.searchableMultiSelect.selectedCount': {
    en: '{count} selected',
    fun: '{count} picked',
  },
  'ui.searchableMultiSelect.removeChipAriaLabel': {
    en: 'Remove {label}',
    fun: 'Yeet {label}',
  },

  // ---- MultiSelect.tsx ----
  'ui.multiSelect.placeholder': { en: 'Select…', fun: 'Pick some…' },
  'ui.multiSelect.clearSelection': {
    en: 'Clear selection',
    fun: 'Wipe it clean',
  },
  'ui.multiSelect.selectedCount': {
    en: '{count} selected',
    fun: '{count} picked',
  },

  // ---- Select.tsx ----
  'ui.select.placeholder': { en: 'Select', fun: 'Pick one' },

  // ---- ColumnFilter.tsx ----
  'ui.columnFilter.filterByLabel': {
    en: 'Filter by {label}',
    fun: 'Sift by {label}',
  },
  'ui.columnFilter.filterColumnAriaLabel': {
    en: 'Filter column',
    fun: 'Sift this column',
  },
  'ui.columnFilter.dialogAriaLabel': {
    en: 'Column filter',
    fun: 'Filter zone',
  },
  'ui.columnFilter.filterLabel': { en: 'Filter', fun: 'Sift' },
  'ui.columnFilter.clear': { en: 'Clear', fun: 'Wipe' },

  // ---- Snackbar.tsx ----
  'ui.snackbar.dismissAriaLabel': { en: 'Dismiss', fun: 'Poof' },

  // ---- Spinner.tsx ----
  'ui.spinner.loadingLabel': { en: 'Loading', fun: 'Cooking' },

  // ---- WorkflowCard.tsx ----
  'ui.workflowCard.runLabel': { en: 'Run', fun: 'Send it' },

  // ---- LogViewer.tsx ----
  'ui.logViewer.emptyLabel': {
    en: 'No logs yet.',
    fun: 'Silence. No logs yet.',
  },

  // ---- Alert.tsx ----
  'ui.alert.dismissAriaLabel': { en: 'Dismiss', fun: 'Poof' },

  // ---- ExternalToolCard.tsx ----
  'ui.externalToolCard.openAriaLabel': {
    en: 'Open {name}',
    fun: 'Launch {name}',
  },

  // ---- DateRangePicker.tsx ----
  'ui.dateRangePicker.ariaLabel': { en: 'Date range', fun: 'Time window' },
  'ui.dateRangePicker.clear': { en: 'Clear date range', fun: 'Wipe the window' },

  // ---- Chip.tsx ----
  'ui.chip.removeAriaLabel': { en: 'Remove', fun: 'Yeet it' },

  // ---- ChipInput.tsx ----
  'ui.chipInput.removeAriaLabel': {
    en: 'Remove {value}',
    fun: 'Toss {value}',
  },

  // ---- CheckboxGroup.tsx ----
  'ui.checkboxGroup.emptyText': {
    en: 'Nothing to choose from.',
    fun: 'Empty box. Nothing to pick.',
  },

  // ---- EnvReleaseTags.tsx ----
  'ui.envReleaseTags.none': { en: 'none', fun: 'nada' },

  // ---- kafka/ConsumerLagTable.tsx + kafka/ServiceConsumesTable.tsx ----
  'ui.kafka.envHeader': { en: 'Env', fun: 'Env' },
  'ui.kafka.clusterHeader': { en: 'Cluster', fun: 'Cluster' },
  'ui.kafka.consumerGroupHeader': {
    en: 'Consumer group',
    fun: 'Consumer group',
  },
  'ui.kafka.serviceHeader': { en: 'Service', fun: 'Service' },
  'ui.kafka.lagHeader': { en: 'Lag', fun: 'Lag' },
  'ui.kafka.topicHeader': { en: 'Topic', fun: 'Topic' },
  'ui.kafka.statusLabel': { en: 'Status', fun: 'Status' },
  'ui.kafka.statusAll': { en: 'All statuses', fun: 'Any vibe' },
  'ui.kafka.statusHealthy': { en: 'Healthy', fun: 'Thriving' },
  'ui.kafka.statusLagging': { en: 'Lagging', fun: 'Dragging' },
  'ui.kafka.statusCritical': { en: 'Critical', fun: 'On fire' },
  'ui.kafka.statusIdle': { en: 'Idle', fun: 'Napping' },
  'ui.kafka.allEnvsPlaceholder': { en: 'All envs', fun: 'Any env' },
  'ui.kafka.searchGroupOrServicePlaceholder': {
    en: 'Search group or service…',
    fun: 'Snoop for a group or service…',
  },
  'ui.kafka.searchTopicOrGroupPlaceholder': {
    en: 'Search topic or group…',
    fun: 'Snoop for a topic or group…',
  },
  'ui.kafka.loadingLabel': {
    en: 'Loading consumer lag…',
    fun: 'Counting the lag…',
  },
  'ui.kafka.emptyMatching': {
    en: 'No matching consumers',
    fun: 'No consumers match that',
  },
  'ui.kafka.emptyTopicsTitle': {
    en: 'No Kafka topics consumed',
    fun: 'Zero Kafka topics consumed',
  },
  'ui.kafka.emptyTopicsDescription': {
    en: 'This service isn’t part of any Kafka consumer group yet, or its lag data hasn’t been collected. Use Refresh to re-check.',
    fun: 'This service isn’t in a Kafka consumer group yet, or the lag data hasn’t landed. Hit Refresh to re-check.',
  },
  'ui.kafka.partitionsLabel': { en: 'Partitions', fun: 'Partitions' },
  'ui.kafka.replicationFactorLabel': {
    en: 'Replication factor',
    fun: 'Replication factor',
  },
  'ui.kafka.clusterFieldLabel': { en: 'Cluster', fun: 'Cluster' },
  'ui.kafka.configSectionLabel': { en: 'Config', fun: 'Config' },

  // ---- deployments/DeploymentsCard.tsx + DeploymentsCardView.tsx ----
  'ui.deployments.title': { en: 'Deployments', fun: 'Deployments' },
  'ui.deployments.emptyMessage': {
    en: 'No deployment data',
    fun: 'No deployment data yet',
  },
  'ui.deployments.description': {
    en: 'Promoted tags and live ArgoCD health per environment.',
    fun: 'Promoted tags and live ArgoCD health per environment.',
  },
  'ui.deployments.refreshTooltip': {
    en: 'Refetch deployment data (promoted tags) and ArgoCD health now. ArgoCD health also auto-refreshes every 60s while this card is visible and pauses when the browser tab is hidden.',
    fun: 'Yanks fresh deployment data (promoted tags) and ArgoCD health right now. ArgoCD health also auto-refreshes every 60s while this card is visible and naps while the tab is hidden.',
  },
  'ui.deployments.environmentUnit': { en: 'environment', fun: 'environment' },
  'ui.deployments.environmentUnitPlural': {
    en: 'environments',
    fun: 'environments',
  },
  'ui.deployments.columnEnvironment': {
    en: 'Environment',
    fun: 'Environment',
  },
  'ui.deployments.columnTag': { en: 'Tag', fun: 'Tag' },
  'ui.deployments.columnSource': { en: 'Source', fun: 'Source' },
  'ui.deployments.columnPublished': { en: 'Published', fun: 'Published' },
  'ui.deployments.columnArgoCD': { en: 'ArgoCD', fun: 'ArgoCD' },
  'ui.deployments.columnObservability': {
    en: 'Observability',
    fun: 'Observability',
  },
  'ui.deployments.columnActions': { en: 'Actions', fun: 'Actions' },
  'ui.deployments.logsLabel': { en: 'Logs', fun: 'Logs' },
  'ui.deployments.tracesLabel': { en: 'Traces', fun: 'Traces' },
  'ui.deployments.profilesLabel': { en: 'Profiles', fun: 'Profiles' },
  'ui.deployments.toggleHistoryAriaLabel': {
    en: 'Toggle deploy history for {env}',
    fun: 'Peek deploy history for {env}',
  },
  'ui.deployments.noHistory': {
    en: 'No promote history recorded',
    fun: 'No promote history — clean slate',
  },
  'ui.deployments.rolloutButton': { en: 'Rollout', fun: 'Send it' },
  'ui.deployments.rolloutTitleApproval': {
    en: 'Rolling restart on {env} — requires approval',
    fun: 'Rolling restart on {env} — needs a thumbs-up',
  },
  'ui.deployments.rolloutTitle': {
    en: 'Rolling restart on {env}',
    fun: 'Rolling restart on {env}',
  },
  'ui.deployments.rolloutAriaLabel': {
    en: 'Roll out {env}',
    fun: 'Send it — {env}',
  },
  'ui.deployments.rolloutStartedTitle': {
    en: 'Rollout started — {env}',
    fun: 'Sent it — {env}',
  },
  'ui.deployments.rolloutApprovalMessage': {
    en: 'Pre-prod and production require approval before the restart runs — track it on the task.',
    fun: 'Pre-prod and production need a thumbs-up before the restart runs — track it on the task.',
  },
  'ui.deployments.viewTaskAction': { en: 'View task', fun: 'Peek the task' },
  'ui.deployments.rolloutFailedTitle': {
    en: 'Rollout failed to start',
    fun: 'Rollout face-planted',
  },

  // ---- Drawer.tsx ----
  'ui.drawer.closeAriaLabel': { en: 'Close', fun: 'Close' },

  // ---- JsonView.tsx ----
  'ui.jsonView.copy': { en: 'Copy', fun: 'Copy' },
  'ui.jsonView.copied': { en: 'Copied', fun: 'Snagged it' },
};
